import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import {
  MessageSquare,
  CheckCircle2,
  Flag,
  Trash2,
  Star,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
} from "lucide-react";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < rating ? "fill-warning text-warning" : "text-muted-foreground/30"}`}
        />
      ))}
    </div>
  );
}

function ReasonForm({
  label,
  variant,
  onConfirm,
  onCancel,
  isPending,
}: {
  label: string;
  variant: "destructive" | "warning";
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <div className="flex items-center gap-2 mt-2">
      <input
        type="text"
        placeholder="Raison…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && reason.trim()) onConfirm(reason.trim());
          if (e.key === "Escape") onCancel();
        }}
      />
      <Button
        size="sm"
        variant={variant === "destructive" ? "destructive" : "outline"}
        disabled={!reason.trim() || isPending}
        onClick={() => onConfirm(reason.trim())}
      >
        {isPending ? "…" : label}
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel} disabled={isPending}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function ReviewModeration() {
  const [page, setPage] = useState(1);
  const [activeAction, setActiveAction] = useState<{ id: string; type: "flag" | "delete" } | null>(null);

  const utils = trpc.useUtils();
  const { data, isLoading, refetch } = trpc.admin.reviews.listPendingReviews.useQuery({
    status: ["PENDING", "FLAGGED"],
    page,
    limit: 20,
  });

  const approve = trpc.admin.reviews.approveReview.useMutation({
    onSuccess: () => utils.admin.reviews.listPendingReviews.invalidate(),
  });

  const flag = trpc.admin.reviews.flagReview.useMutation({
    onSuccess: () => {
      setActiveAction(null);
      utils.admin.reviews.listPendingReviews.invalidate();
    },
  });

  const del = trpc.admin.reviews.deleteReview.useMutation({
    onSuccess: () => {
      setActiveAction(null);
      utils.admin.reviews.listPendingReviews.invalidate();
    },
  });

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
              <MessageSquare className="h-7 w-7" />
              Modération des avis
            </h1>
            <p className="text-muted-foreground">
              Avis en attente et signalés — {data?.total ?? 0} au total
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-base">Avis à modérer</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !data?.reviews.length ? (
              <div className="p-6 text-center text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-success" />
                Aucun avis en attente de modération
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-3 font-medium">Auteur</th>
                      <th className="text-left px-4 py-3 font-medium">Entreprise</th>
                      <th className="text-left px-4 py-3 font-medium">Projet</th>
                      <th className="text-center px-4 py-3 font-medium">Note</th>
                      <th className="text-left px-4 py-3 font-medium">Commentaire</th>
                      <th className="text-center px-4 py-3 font-medium">Date</th>
                      <th className="text-center px-4 py-3 font-medium">Statut</th>
                      <th className="text-right px-4 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.reviews.map((review: any) => (
                      <tr key={review.id} className="hover:bg-muted/30 transition-colors align-top">
                        <td className="px-4 py-3">
                          <div className="font-medium">
                            {review.Author?.firstName} {review.Author?.lastName}
                          </div>
                          <div className="text-xs text-muted-foreground">{review.Author?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {review.ArtisanCompany?.name ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground max-w-[140px] truncate">
                          {review.PrivateProject?.title ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StarRating rating={review.overallRating ?? review.rating ?? 0} />
                        </td>
                        <td className="px-4 py-3 max-w-[240px]">
                          <p className="line-clamp-2 text-muted-foreground">{review.comment ?? "—"}</p>
                          {activeAction?.id === review.id && activeAction.type === "flag" && (
                            <ReasonForm
                              label="Signaler"
                              variant="warning"
                              isPending={flag.isPending}
                              onConfirm={(reason) => flag.mutate({ reviewId: review.id, reason })}
                              onCancel={() => setActiveAction(null)}
                            />
                          )}
                          {activeAction?.id === review.id && activeAction.type === "delete" && (
                            <ReasonForm
                              label="Supprimer"
                              variant="destructive"
                              isPending={del.isPending}
                              onConfirm={(reason) => del.mutate({ reviewId: review.id, reason })}
                              onCancel={() => setActiveAction(null)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(review.createdAt).toLocaleDateString("fr-FR")}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge
                            variant={review.moderationStatus === "FLAGGED" ? "destructive" : "secondary"}
                          >
                            {review.moderationStatus}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-success hover:text-success"
                              onClick={() => approve.mutate({ reviewId: review.id })}
                              disabled={approve.isPending}
                              title="Approuver"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-warning hover:text-warning"
                              onClick={() => setActiveAction({ id: review.id, type: "flag" })}
                              title="Signaler"
                            >
                              <Flag className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setActiveAction({ id: review.id, type: "delete" })}
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {data && data.totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-sm text-muted-foreground">
                  Page {data.page} / {data.totalPages} — {data.total} avis
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
